import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '../../../../api/client';
import { fmtDateTime } from './utils';
import {
  REFERRAL_REASON_LABEL,
  REFERRAL_STATUS_LABEL,
  type MyReferralCode,
  type ReferralRecordItem,
} from './types';

/** 邀请数据查询键 */
const REFERRAL_KEYS = {
  myCode: ['referral', 'my-code'] as const,
  records: ['referral', 'records'] as const,
};

/**
 * 构造可分享的邀请链接：指向站点首页并带 ?ref=，
 * 好友打开后由全局 useReferralCapture 捕获并（登录后）绑定。
 */
function buildInviteLink(code: string): string {
  return `${window.location.origin}/?ref=${code}`;
}

/**
 * 邀请减免面板（邀请人视角）：
 * - 展示我的邀请码、累计统计（已邀请/已计酬/累计获得月数）；
 * - 一键复制邀请码 / 复制邀请链接；
 * - 我的邀请记录（状态、奖励月数、不计酬原因，不暴露被邀请人身份）。
 *
 * 数据来自 GET /referral/my-code 与 GET /referral/records（React Query 缓存）。
 */
export function InvitePanel() {
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  const myCode = useQuery({
    queryKey: REFERRAL_KEYS.myCode,
    queryFn: () => get<MyReferralCode>('/referral/my-code'),
    staleTime: 60_000,
  });

  const records = useQuery({
    queryKey: REFERRAL_KEYS.records,
    queryFn: () => get<ReferralRecordItem[]>('/referral/records'),
    staleTime: 30_000,
  });

  const data = myCode.data;
  const inviteLink = useMemo(() => (data?.code ? buildInviteLink(data.code) : ''), [data?.code]);

  const copy = async (text: string, kind: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // 剪贴板 API 不可用（非安全上下文/权限受限）时回落到临时输入框选中复制
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* ignore */
      }
      document.body.removeChild(ta);
    }
    setCopied(kind);
    setTimeout(() => setCopied(null), 1800);
  };

  if (myCode.isLoading) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3">邀请好友</h3>
        <div className="border border-gray-200 rounded-xl p-6 text-center text-gray-400 text-xs">加载中…</div>
      </div>
    );
  }

  // 后端关闭邀请功能时给出说明，不展示邀请码
  if (data && !data.enabled) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3">邀请好友</h3>
        <div className="border border-gray-200 rounded-xl p-5 text-center text-xs text-gray-500 bg-gray-50">
          <i className="ri-gift-line mr-1" />
          邀请减免活动暂未开启，敬请期待
        </div>
      </div>
    );
  }

  const recordList = records.data ?? [];

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-3">邀请好友，共享减免</h3>

      <div className="border border-amber-200 rounded-xl overflow-hidden bg-gradient-to-br from-amber-50 to-yellow-50">
        {/* 邀请码 + 分享 */}
        <div className="p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs text-amber-600 mb-1">我的邀请码</p>
              <p className="text-2xl font-bold tracking-[0.2em] text-amber-700">{data?.code ?? '——'}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!data?.code}
                onClick={() => data && copy(data.code, 'code')}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-white border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={copied === 'code' ? 'ri-check-line' : 'ri-file-copy-line'} />
                {copied === 'code' ? '已复制' : '复制邀请码'}
              </button>
              <button
                type="button"
                disabled={!inviteLink}
                onClick={() => copy(inviteLink, 'link')}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={copied === 'link' ? 'ri-check-line' : 'ri-share-forward-line'} />
                {copied === 'link' ? '已复制' : '复制邀请链接'}
              </button>
            </div>
          </div>

          <p className="text-xs text-amber-600/80 mt-3">
            <i className="ri-information-line mr-1" />
            好友通过你的邀请码注册并付费开通会员后，你将获得会员时长奖励，好友下单也可享专属减免。
          </p>
        </div>

        {/* 累计统计 */}
        <div className="grid grid-cols-3 divide-x divide-amber-200 border-t border-amber-200 bg-white/50">
          <Stat label="已邀请" value={data?.invitedCount ?? 0} unit="人" />
          <Stat label="已计酬" value={data?.rewardedCount ?? 0} unit="人" />
          <Stat label="累计获得" value={data?.earnedMonths ?? 0} unit="个月" highlight />
        </div>
      </div>

      {/* 邀请记录 */}
      <div className="mt-4">
        <p className="text-xs font-medium text-gray-600 mb-2">我的邀请记录</p>
        {records.isLoading ? (
          <div className="border border-gray-200 rounded-xl p-4 text-center text-gray-400 text-xs">加载中…</div>
        ) : recordList.length === 0 ? (
          <div className="border border-dashed border-gray-200 rounded-xl p-5 text-center text-xs text-gray-400">
            还没有邀请记录，快把邀请码分享给好友吧
          </div>
        ) : (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-3 py-2 text-gray-500 font-medium">时间</th>
                  <th className="text-center px-3 py-2 text-gray-500 font-medium">状态</th>
                  <th className="text-center px-3 py-2 text-gray-500 font-medium">奖励</th>
                  <th className="text-left px-3 py-2 text-gray-500 font-medium">说明</th>
                </tr>
              </thead>
              <tbody>
                {recordList.slice(0, 10).map((r) => {
                  const badge = REFERRAL_STATUS_LABEL[r.status] ?? { text: r.status, cls: 'bg-gray-100 text-gray-500' };
                  const note = r.reason ? REFERRAL_REASON_LABEL[r.reason] ?? r.reason : '—';
                  return (
                    <tr key={r.id} className="border-b border-gray-100 last:border-0">
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtDateTime(r.createdAt)}</td>
                      <td className="text-center px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.text}</span>
                      </td>
                      <td className="text-center px-3 py-2 text-gray-700">
                        {r.rewardMonths > 0 ? `+${r.rewardMonths} 个月` : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-500">{note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** 单个统计项 */
function Stat({ label, value, unit, highlight }: { label: string; value: number; unit: string; highlight?: boolean }) {
  return (
    <div className="px-3 py-3 text-center">
      <p className={`text-lg font-bold ${highlight ? 'text-amber-600' : 'text-gray-800'}`}>
        {value}
        <span className="text-xs font-medium text-gray-400 ml-0.5">{unit}</span>
      </p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}
